import React, { useMemo } from 'react';

import { renderBulletOrEmpty } from 'components/molecules/bidding_summary_bullet_list';

import type { DocumentRequirementItem } from 'store/modules/bidding_summary_store/types';

interface BiddingSummaryDocumentRequirementsProps {
  items?: (string | DocumentRequirementItem)[];
  scope: string;
}

/**
 * documentos_habilitacao/anexos_declaracoes/atestado_capacidade_tecnica podem vir como string
 * (legado) ou objeto `{documento, trecho_edital, confianca}` (novo). O `trecho_edital` não é
 * exibido aqui de propósito — será consumido em outro lugar (funcionalidade futura de
 * verificação); esta tela mostra só a lista de documentos, com um indicador quando a
 * classificação teve baixa confiança.
 */
const BiddingSummaryDocumentRequirements: React.FC<BiddingSummaryDocumentRequirementsProps> = ({
  items,
  scope,
}) => {
  const { legacyList, structuredList } = useMemo(() => {
    if (!Array.isArray(items)) {
      return {
        legacyList: [] as string[],
        structuredList: [] as DocumentRequirementItem[],
      };
    }

    const legacy = items
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);

    const structured = items.filter(
      (item): item is DocumentRequirementItem => Boolean(item) && typeof item === 'object',
    );

    return { legacyList: legacy, structuredList: structured };
  }, [items]);

  if (!structuredList.length) {
    return <>{renderBulletOrEmpty(scope, legacyList)}</>;
  }

  return (
    <>
      <ul className="pl-4 mb-0">
        {structuredList.map((item, index) => {
          const documento = item.documento?.trim();
          if (!documento) return null;
          const isBaixaConfianca = item.confianca === 'baixa';

          return (
            <li key={`${scope}::structured::${index}`}>
              {documento}
              {isBaixaConfianca ? (
                <span className="badge badge-warning ml-2">a confirmar</span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {legacyList.length ? (
        <div className="mt-2">{renderBulletOrEmpty(`${scope}-legacy`, legacyList)}</div>
      ) : null}
    </>
  );
};

export default BiddingSummaryDocumentRequirements;
